/**
 * Jazia Toast: success and error notifications only.
 * Global reusable helper — call from any client-side UI.
 *
 *   JaziaToast.success("Sale completed", "Receipt sent to the tax authority");
 *   JaziaToast.error("Payment failed", "The customer has insufficient funds");
 *
 * Options (third argument, or pass a single object):
 *   { title, message, duration }   duration in ms, 0 keeps the toast until closed
 *
 * Each call returns a function that closes that toast.
 * JaziaToast.clear() closes all toasts.
 */

type ToastType = "success" | "error";

type ToastOptions = {
  title?: string;
  message?: string;
  duration?: number;
};

const MAX_VISIBLE = 4;
const DEFAULT_DURATION: Record<ToastType, number> = { success: 4000, error: 6000 };

const CSS = [
  ".jz-toasts{--jz-bg:#fff;--jz-text:#2A2420;--jz-muted:#76665B;--jz-line:#EFDDCF;",
  "--jz-ok:#2F6B3A;--jz-ok-bg:#E3F1E3;--jz-err:#B3401B;--jz-err-bg:#FDE3D8;--jz-shadow:0 8px 28px rgba(42,36,32,.14);",
  "position:fixed;z-index:2147483000;top:calc(16px + env(safe-area-inset-top,0px));right:16px;display:flex;flex-direction:column;gap:10px;",
  "width:min(380px,calc(100vw - 32px));pointer-events:none;font-family:inherit}",
  ".jz-toast{position:relative;overflow:hidden;pointer-events:auto;display:flex;align-items:flex-start;gap:12px;padding:14px 12px 16px 14px;",
  "background:var(--jz-bg);color:var(--jz-text);border:1px solid var(--jz-line);border-radius:16px;box-shadow:var(--jz-shadow);",
  "font-size:15px;line-height:1.4;animation:jz-in .28s cubic-bezier(.2,.8,.2,1) both}",
  ".jz-out{animation:jz-out .2s ease-in both}",
  ".jz-icon{flex:none;width:32px;height:32px;border-radius:50%;display:grid;place-items:center}",
  ".jz-success .jz-icon{background:var(--jz-ok-bg);color:var(--jz-ok)}",
  ".jz-error .jz-icon{background:var(--jz-err-bg);color:var(--jz-err)}",
  ".jz-body{flex:1;min-width:0;padding-top:4px}",
  ".jz-title{font-weight:600;margin:0;overflow-wrap:anywhere}",
  ".jz-msg{margin:2px 0 0;color:var(--jz-muted);font-size:14px;overflow-wrap:anywhere}",
  ".jz-close{flex:none;width:32px;height:32px;margin:0;padding:0;border:0;border-radius:50%;background:transparent;color:var(--jz-muted);cursor:pointer;display:grid;place-items:center}",
  ".jz-close:hover{background:var(--jz-line)}",
  ".jz-close:focus-visible{outline:2px solid var(--jz-text);outline-offset:1px}",
  ".jz-bar{position:absolute;left:0;bottom:0;height:3px;width:100%;transform-origin:left;animation:jz-bar linear both}",
  ".jz-success .jz-bar{background:var(--jz-ok)}.jz-error .jz-bar{background:var(--jz-err)}",
  ".jz-paused .jz-bar{animation-play-state:paused}",
  "@keyframes jz-in{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:none}}",
  "@keyframes jz-out{to{opacity:0;transform:translateX(24px)}}",
  "@keyframes jz-bar{from{transform:scaleX(1)}to{transform:scaleX(0)}}",
  "@media(max-width:560px){.jz-toasts{top:auto;right:50%;transform:translateX(50%);bottom:calc(16px + env(safe-area-inset-bottom,0px));flex-direction:column-reverse}",
  "@keyframes jz-in{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}",
  "@keyframes jz-out{to{opacity:0;transform:translateY(24px)}}}",
  "@media(prefers-reduced-motion:reduce){.jz-toast,.jz-out{animation-duration:.01ms}.jz-bar{display:none}}",
].join("");

const SVG =
  'width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';

const ICON: Record<ToastType | "close", string> = {
  success: `<svg ${SVG}><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>`,
  error: `<svg ${SVG}><path d="M6 6l12 12M18 6L6 18"/></svg>`,
  close:
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
};

let container: HTMLDivElement | null = null;

function ensure() {
  if (typeof document === "undefined") return null;
  if (container?.isConnected) return container;

  if (!document.getElementById("jz-toast-style")) {
    const style = document.createElement("style");
    style.id = "jz-toast-style";
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  container = document.createElement("div");
  container.className = "jz-toasts";
  document.body.appendChild(container);
  return container;
}

function normalise(type: ToastType, a?: string | ToastOptions, b?: string | ToastOptions, c?: ToastOptions) {
  let options: ToastOptions = {};

  if (a && typeof a === "object") {
    options = a;
  } else {
    options.title = a;
    if (b && typeof b === "object") {
      options.duration = b.duration;
    } else {
      options.message = b;
    }
    if (c && typeof c === "object" && c.duration !== undefined) {
      options.duration = c.duration;
    }
  }

  return {
    title: String(options.title ?? ""),
    message: options.message == null ? "" : String(options.message),
    duration: typeof options.duration === "number" ? options.duration : DEFAULT_DURATION[type],
  };
}

function show(type: ToastType, a?: string | ToastOptions, b?: string | ToastOptions, c?: ToastOptions) {
  const options = normalise(type, a, b, c);
  if (!options.title && !options.message) return () => undefined;

  const box = ensure();
  if (!box) return () => undefined;

  while (box.children.length >= MAX_VISIBLE) {
    box.removeChild(box.firstChild as Node);
  }

  const el = document.createElement("div");
  el.className = `jz-toast jz-${type}`;
  el.setAttribute("role", type === "error" ? "alert" : "status");

  const icon = document.createElement("div");
  icon.className = "jz-icon";
  icon.innerHTML = ICON[type];

  const body = document.createElement("div");
  body.className = "jz-body";

  if (options.title) {
    const title = document.createElement("p");
    title.className = "jz-title";
    title.textContent = options.title;
    body.appendChild(title);
  }

  if (options.message) {
    const message = document.createElement("p");
    message.className = "jz-msg";
    message.textContent = options.message;
    body.appendChild(message);
  }

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "jz-close";
  btn.setAttribute("aria-label", "Dismiss notification");
  btn.innerHTML = ICON.close;

  el.appendChild(icon);
  el.appendChild(body);
  el.appendChild(btn);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let remaining = options.duration;
  let started = 0;
  let closed = false;

  if (options.duration > 0) {
    const bar = document.createElement("div");
    bar.className = "jz-bar";
    bar.style.animationDuration = `${options.duration}ms`;
    el.appendChild(bar);
  }

  const close = () => {
    if (closed) return;
    closed = true;
    if (timer) clearTimeout(timer);
    el.classList.add("jz-out");
    const done = () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    el.addEventListener("animationend", done);
    setTimeout(done, 260);
  };

  const start = () => {
    if (!(remaining > 0)) return;
    started = Date.now();
    timer = setTimeout(close, remaining);
    el.classList.remove("jz-paused");
  };

  const pause = () => {
    if (!(remaining > 0) || closed) return;
    if (timer) clearTimeout(timer);
    remaining -= Date.now() - started;
    el.classList.add("jz-paused");
  };

  btn.addEventListener("click", close);
  el.addEventListener("mouseenter", pause);
  el.addEventListener("mouseleave", start);
  el.addEventListener("focusin", pause);
  el.addEventListener("focusout", start);
  el.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });

  box.appendChild(el);
  start();
  return close;
}

export const JaziaToast = {
  success(a?: string | ToastOptions, b?: string | ToastOptions, c?: ToastOptions) {
    return show("success", a, b, c);
  },
  error(a?: string | ToastOptions, b?: string | ToastOptions, c?: ToastOptions) {
    return show("error", a, b, c);
  },
  clear() {
    if (!container) return;
    while (container.firstChild) container.removeChild(container.firstChild);
  },
};

if (typeof window !== "undefined") {
  (window as Window & { JaziaToast?: typeof JaziaToast }).JaziaToast = JaziaToast;
}
